import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../../context/LanguageContext";
import CustomCarouselSliders from "../../components/customslider/customslider";
import FilterRenderer from "../../components/filter/FilterRenderer";
import { BiSolidCategory } from "react-icons/bi";
import { getLimitedImages } from "../../config/pageDetails";
import { buildProductsFilterPayload } from "./productFilterPayload";
import "./products.css";

const CustomSlider = ({ slide }) => {
  const images = getLimitedImages(slide?.imageUrl, "product", "cardImageLimit");

  return (
    <div>
      <CustomCarouselSliders>
        {images.map((image, index) => (
          <img
            className="image-category-slider"
            key={index}
            src={image}
            alt={slide.name}
          />
        ))}
      </CustomCarouselSliders>
    </div>
  );
};

const Products = () => {
  const { t } = useContext(LanguageContext);
  const location = useLocation();
  const subCategory = location.state?.subCategory;
  const categoryId = subCategory?.categoryId;
  const subCategoryId = subCategory?.subCategoryId;

  const searchInputRef = React.useRef(null);

  const navigate = useNavigate();

  const [productsData, setProductsData] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const pageSize = 12;

  const [selectedFilters, setSelectedFilters] = useState({});

  const metaData = useMemo(() => {
    if (!subCategory?.metaData) {
      return [];
    }

    try {
      const parsedMeta =
        typeof subCategory.metaData === "string"
          ? JSON.parse(subCategory.metaData)
          : subCategory.metaData;

      return Array.isArray(parsedMeta) ? parsedMeta : [];
    } catch (e) {
      console.error("Failed to parse subCategory.metaData", e);
      return [];
    }
  }, [subCategory]);

  const requestObject = useMemo(
    () => ({
      categoryId,
      subCategoryId,
      filter: buildProductsFilterPayload(selectedFilters, metaData),
      getCount: true,
    }),
    [categoryId, subCategoryId, metaData, selectedFilters]
  );

  const fetchData = useCallback(async () => {
    if (!categoryId || !subCategoryId) {
      return;
    }

    try {
      const response = await fetch(
        `/api/products?offset=${currentPage}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestObject),
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch subcategory data");
        return;
      }

      const result = await response.json();
      setProductsData(result.products);
      setTotalCount(result.totalCount || 0);
    } catch (err) {
      console.error(err);
    }
  }, [categoryId, currentPage, requestObject, subCategoryId]);

  useEffect(() => {
    if (!subCategory) {
      navigate(`/category`);
      return;
    }

    if (!categoryId) {
      navigate(`/category`);
      return;
    }

    if (!subCategoryId) {
      navigate(`/category`);
      return;
    }
  }, [categoryId, navigate, subCategory, subCategoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePrev = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleMenuClick = (product) => {
    navigate("/product/" + product.productId, { state: { product } });
  };

  const handleFilterChange = useCallback((filterName, value) => {
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev };

      if (value === undefined || value === null || value === "") {
        delete updatedFilters[filterName];
      } else {
        updatedFilters[filterName] = value;
      }

      return updatedFilters;
    });
    setCurrentPage(0);
  }, []);

  return (
    <div className="category-container">
      <div className="category-header">
        <BiSolidCategory className="category-icon" />
        <h2 className="category-header-title">
          {subCategory && subCategory.name ? subCategory.name : ""}
        </h2>
      </div>

      <div className="filter-section-products">
        {metaData.map(
          (filterOption, index) =>
            filterOption.isActive && (
              <div key={index} className="product-filter-container">
                <FilterRenderer
                  filterOption={filterOption}
                  selectedFilters={selectedFilters}
                  onFilterChange={handleFilterChange}
                  searchInputRef={searchInputRef}
                />
              </div>
            )
        )}
      </div>

      <div className="pagination-controls">
        <button
          className="card-button"
          onClick={handlePrev}
          disabled={currentPage === 0}
        >
          {t(`constants.Previous`)}
        </button>
        <span style={{ margin: "0 10px" }}>
          {t(`constants.Page`)} {currentPage + 1} {t(`constants.of`)}{" "}
          {totalPages}
        </span>
        <button
          className="card-button"
          onClick={handleNext}
          disabled={currentPage >= totalPages - 1}
        >
          {t(`constants.Next`)}
        </button>
      </div>

      <div className="category-grid">
        {productsData &&
          productsData.map((product, index) => (
            <div className="category-card" key={index}>
              <CustomSlider slide={product} />
              <div className="card-body">
                <div className="card-title">{product.name}</div>
                <div className="card-description">{product.description}</div>
                <button
                  className="card-button"
                  onClick={() => handleMenuClick(product)}
                >
                  {t(`products.viewProduct`)}
                </button>
              </div>
            </div>
          ))}
      </div>

      <div className="pagination-controls">
        <button
          className="card-button"
          onClick={handlePrev}
          disabled={currentPage === 0}
        >
          {t(`constants.Previous`)}
        </button>
        <span style={{ margin: "0 10px" }}>
          {t(`constants.Page`)} {currentPage + 1} {t(`constants.of`)}{" "}
          {totalPages}
        </span>
        <button
          className="card-button"
          onClick={handleNext}
          disabled={currentPage >= totalPages - 1}
        >
          {t(`constants.Next`)}
        </button>
      </div>
    </div>
  );
};

export default Products;
